import { isChildUser, isParentUser } from 'src/utils/userRole';
import { isNative } from 'src/utils/platform';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { OneSignal } from 'src/modules/push-notifications/onesignal-instance';
import type { InAppMessageClickEvent } from 'onesignal-cordova-plugin';
import { useCurrentUser } from 'src/queries/user';
import type { User } from 'src/types/user.types';
import { ROUTES } from 'src/config/routes';
import FeedbackModal from 'src/components/Notifications/FeedbackModal';
import { hasOwn } from 'src/utils/hasOwn';
import { queryKeys } from 'src/queries/keys';

const OPEN_FEEDBACK_ACTION_ID = 'open_feedback';
/** Must match the action ID configured on the OneSignal campaign button. */
const OPEN_SCHOOL_LEVEL_SETTINGS_ACTION_ID = 'open_school_level_settings';
/** Must match the action ID configured on the OneSignal campaign buttons. */
const OPEN_TIMETABLE_UPLOAD_ACTION_ID = 'open_timetable_upload';
export const TIMETABLE_IAM_PATH = `${ROUTES.CHILD_TIMETABLE.path}?source=iam`;

const InAppMessageClickHost = () => {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  // The listener registers once, so handlers read through refs.
  const userRef = useRef(user);
  // eslint-disable-next-line react-hooks/refs
  userRef.current = user;
  const navigateRef = useRef(navigate);
  // eslint-disable-next-line react-hooks/refs
  navigateRef.current = navigate;
  const queryClientRef = useRef(queryClient);
  // eslint-disable-next-line react-hooks/refs
  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!isNative()) {
      return;
    }
    const actionHandlers: Record<string, () => void> = {
      [OPEN_FEEDBACK_ACTION_ID]: () => setIsFeedbackOpen(true),
      [OPEN_SCHOOL_LEVEL_SETTINGS_ACTION_ID]: () => {
        if (!isChildUser(userRef.current)) {
          return;
        }
        navigateRef.current(ROUTES.CHILD_EDIT_PROFILE_SCHOOL_LEVEL.path);
      },
      [OPEN_TIMETABLE_UPLOAD_ACTION_ID]: () => {
        const current = userRef.current;
        if (!current) {
          return;
        }
        if (isParentUser(current)) {
          const users = queryClientRef.current.getQueryData<User[]>(
            queryKeys.users(),
          );
          if (users !== undefined) {
            const hasChild = users.some(
              u => isChildUser(u) && !!u.firstname,
            );
            if (!hasChild) {
              return;
            }
          }
        }
        navigateRef.current(TIMETABLE_IAM_PATH);
      },
    };
    const handleClick = (event: InAppMessageClickEvent) => {
      const actionId = event.result.actionId ?? '';
      if (!hasOwn(actionHandlers, actionId)) {
        return;
      }
      actionHandlers[actionId]();
    };
    OneSignal.InAppMessages.addEventListener('click', handleClick);
    return () =>
      OneSignal.InAppMessages.removeEventListener('click', handleClick);
  }, []);

  const familyCode = user?.family?.join_code;
  if (!isFeedbackOpen || !familyCode) {
    return null;
  }

  return (
    <FeedbackModal
      familyCode={familyCode}
      language={i18n.language}
      country={user.country}
      onClose={() => setIsFeedbackOpen(false)}
    />
  );
};

export default InAppMessageClickHost;
